import { anthropicLLM } from './anthropic';
import { groqLLM } from './groq';
import { geminiLLM } from './gemini';
import { openaiLLM } from './openai';
import { LLMProvider, Component } from '../types';
import { logger } from '../utils/logger';

/**
 * Generate next questions based on the original user prompt and generated component
 * Routes to the appropriate LLM provider (Anthropic or Groq)
 * Falls back to next provider if current provider fails or returns empty results
 */
export async function generateNextQuestions(
	originalUserPrompt: string,
	component: Component,
	componentData?: Record<string, unknown>,
	anthropicApiKey?: string,
	groqApiKey?: string,
	geminiApiKey?: string,
	openaiApiKey?: string,
	llmProviders?: LLMProvider[],
	conversationHistory?: string
): Promise<string[]> {
	try {
		logger.debug('[generateNextQuestions] Starting next questions generation');
		logger.debug(`[generateNextQuestions] User prompt: "${originalUserPrompt?.substring(0, 50)}..."`);
		logger.debug(`[generateNextQuestions] Component: ${component?.name || 'unknown'} (${component?.type || 'unknown'})`);
		logger.debug(`[generateNextQuestions] Component data available: ${componentData ? 'yes' : 'no'}`);

		// Determine which providers to use
		const providers = llmProviders || ['anthropic', 'gemini', 'openai', 'groq'];
		logger.info(`[generateNextQuestions] Using LLM providers: [${providers.join(', ')}]`);

		// Log conversation context info
		if (conversationHistory && conversationHistory.length > 0) {
			const exchangeCount = conversationHistory.split('\n').filter((l: string) => l.startsWith('Q')).length;
			logger.debug(`[generateNextQuestions] Using conversation history with ${exchangeCount} previous exchanges`);
		} else {
			logger.debug('[generateNextQuestions] No conversation history available');
		}

		// Try each provider in order
		for (let i = 0; i < providers.length; i++) {
			const provider = providers[i];
			const isLastProvider = i === providers.length - 1;

			try {
				logger.info(`[generateNextQuestions] Attempting provider: ${provider} (${i + 1}/${providers.length})`);

				let result: string[] = [];

				if (provider === 'groq') {
					logger.debug('[generateNextQuestions] Using Groq LLM for next questions');
					result = await groqLLM.generateNextQuestions(
						originalUserPrompt,
						component,
						componentData,
						groqApiKey,
						conversationHistory
					);
				} else if (provider === 'gemini') {
					logger.debug('[generateNextQuestions] Using Gemini LLM for next questions');
					result = await geminiLLM.generateNextQuestions(
						originalUserPrompt,
						component,
						componentData,
						geminiApiKey,
						conversationHistory
					);
				} else if (provider === 'openai') {
					logger.debug('[generateNextQuestions] Using OpenAI LLM for next questions');
					result = await openaiLLM.generateNextQuestions(
						originalUserPrompt,
						component,
						componentData,
						openaiApiKey,
						conversationHistory
					);
				} else {
					// Default to Anthropic
					logger.debug('[generateNextQuestions] Using Anthropic LLM for next questions');
					result = await anthropicLLM.generateNextQuestions(
						originalUserPrompt,
						component,
						componentData,
						anthropicApiKey,
						conversationHistory
					);
				}

				// If we got results, return them
				if (result && result.length > 0) {
					logger.info(`[generateNextQuestions] Successfully generated ${result.length} questions with ${provider}`);
					logger.debug(`[generateNextQuestions] Questions: ${JSON.stringify(result)}`);
					return result;
				}

				logger.warn(`[generateNextQuestions] No questions generated from ${provider}${!isLastProvider ? ', trying next provider...' : ''}`);
			} catch (providerError) {
				const errorMsg = providerError instanceof Error ? providerError.message : String(providerError);
				logger.error(`[generateNextQuestions] Provider ${provider} failed: ${errorMsg}`);
				logger.debug(`[generateNextQuestions] Provider error details:`, providerError);

				if (!isLastProvider) {
					logger.info(`[generateNextQuestions] Provider ${provider} failed, trying next provider...`);
				}
				// Continue to next provider
				continue;
			}
		}

		// All providers failed or returned empty results
		logger.warn('[generateNextQuestions] All providers failed or returned no questions');
		return [];
	} catch (error) {
		const errorMsg = error instanceof Error ? error.message : String(error);
		const errorStack = error instanceof Error ? error.stack : undefined;

		logger.error(`[generateNextQuestions] Error generating next questions: ${errorMsg}`);
		logger.debug('[generateNextQuestions] Error stack trace:', errorStack);
		// Return empty array on error
		return [];
	}
}
